import { MotionGlobalConfig } from "framer-motion";
import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useSearchParams, useLocation } from "react-router-dom";
import * as VS from "./services/ValidatorService";
import * as TerminalService from "./services/data/TerminalService";
import * as TerminalAppService from "./services/data/TerminalAppService";
import * as PatientService from "./services/data/PatientService";
import * as Helper from "./_Helper";
import * as Const from "./_Const";
import useAxios from "./services/useAxios";
import { useLocalStorage, useSessionStorage, useStorage } from "./hooks/useStorage";
import useTimeout from "./hooks/useTimeout";
import useDetectScreenOff from "./hooks/useDetectScreenOff";
import useDarkMode from "./hooks/useDarkMode";
import useErrorState from "./hooks/useErrorState";
import useMqttClient from "./hooks/useMqttClient";
import useWindowSize from "./hooks/useWindowSize";
import localforage from "localforage";
import toast from "react-hot-toast";
import Dialog from "./components/Dialog";

const e_screen_resolution_restart =
  "Bylo zjištěno chybné rozlišení obrazovky %1x%2 - očekávané rozlišení má být %3x%4\n\nZa 5 vteřin dojde k restartu zařízení.";
const e_screen_resolution = "Bylo zjištěno chybné rozlišení obrazovky %1x%2 - očekávané rozlišení má být %3x%4\n\nKontaktujte správce zařízení.";

const FormContext = React.createContext();

export function useFormCtx() {
  return useContext(FormContext);
}

const registrationData = [
  {
    stepIndex: 0,
    route: "",
    title: "Úvodní obrazovka",
    subtitle: "",
    showNext: false,
    showBack: false,
    nextRoute: Const.REG_01,
    nextTitle: "",
    showNextIcon: false,
    showSteps: false,
  },
  {
    stepIndex: Const.REG_01_INDEX,
    route: Const.REG_01,
    title: "Důvod návštěvy",
    subtitle: "Zvolte důvod návštěvy",
    showNext: false,
    showBack: true,
    nextRoute: Const.REG_02,
    nextTitle: "",
    showNextIcon: false,
    showSteps: true,
  },
  {
    stepIndex: Const.REG_02_INDEX,
    route: Const.REG_02,
    title: "Osobní údaje",
    subtitle: "Vyplňte níže uvedené údaje",
    showNext: true,
    showBack: false,
    nextTitle: "Registrovat",
    nextRoute: Const.REG_SUMMARY,
    showNextIcon: false,
    showSteps: true,
  },
];

const registrationDataRpi = [
  {
    stepIndex: 0,
    route: "",
    title: "Úvodní obrazovka",
    subtitle: "",
    showNext: false,
    showBack: false,
    nextRoute: Const.REG_INSERT_CARD,
    nextTitle: "",
    showNextIcon: false,
    showSteps: false,
    pageId: Const.PAGE_TERMINAL_ID_HOME,
  },
  {
    stepIndex: Const.REG_RPI_INSERT_CARD_INDEX,
    route: Const.REG_INSERT_CARD,
    title: "Vítejte v čekárně",
    subtitle: "Vložte průkaz totožnosti do zařízení",
    showNext: false,
    showBack: false,
    nextRoute: Const.REG_REASON_BUTTON,
    nextTitle: "",
    showNextIcon: false,
    showSteps: false,
    pageId: Const.PAGE_TERMINAL_ID_INSERT_CARD,
  },
  {
    stepIndex: Const.REG_RPI_REASON_BUTTON_INDEX,
    route: Const.REG_REASON_BUTTON,
    title: "Důvod návštěvy",
    subtitle: "Zvolte důvod návštěvy",
    showNext: false,
    showBack: true,
    nextRoute: Const.REG_REMOVE_CARD,
    nextTitle: "",
    showNextIcon: false,
    showSteps: false,
    pageId: Const.PAGE_TERMINAL_ID_BUTTONS,
  },
  /*
  {
    stepIndex: Const.REG_RPI_INUSRANCE_COMPANY_INDEX,
    route: Const.REG_INUSRANCE,
    title: "Osobní údaje - pojišťovna",
    subtitle: "Vyberte prosím Vaši pojišťovnu",
    showNext: false,
    showBack: false,
    nextRoute: Const.REG_PERSONAL_NUMBER,
    nextTitle: "",
    showNextIcon: false,
    showSteps: true,
  },
  {
    stepIndex: Const.REG_RPI_PERSONAL_NUMBER_INDEX,
    route: Const.REG_PERSONAL_NUMBER,
    title: "Osobní údaje - rodné číslo",
    subtitle: "Vyplňte prosím Vaše rodné číslo",
    showNext: true,
    showBack: false,
    nextTitle: "Další stránka",
    nextRoute: Const.REG_LAST_NAME,
    showNextIcon: true,
    showSteps: true,
  },
  {
    stepIndex: Const.REG_RPI_LAST_NAME_INDEX,
    route: Const.REG_LAST_NAME,
    title: "Osobní údaje - příjmení",
    subtitle: "Vyplňte prosím Vaše příjmení",
    showNext: true,
    showBack: false,
    nextTitle: "Další stránka",
    nextRoute: Const.REG_NAME,
    showNextIcon: true,
    showSteps: true,
  },
  {
    stepIndex: Const.REG_RPI_NAME_INDEX,
    route: Const.REG_NAME,
    title: "Osobní údaje - jméno",
    subtitle: "Vyplňte prosím Vaše jméno",
    showNext: true,
    showBack: false,
    nextTitle: "Registrovat se do systému",
    nextRoute: Const.REG_SUMMARY,
    showNextIcon: false,
    showSteps: true,
  },*/
];

const defaultState = {
  isWithoutCard: false,
  queueId: 0,
  pid: 0,
  identification: "",
  isSetByDefault: false,
  queueName: "",
  queueFullName: "",
  personalNumber: "",
  lastName: "",
  firstName: "",
  email: "",
  insurance: "",
  errors: { queueId: 0, personalNumber: "", lastName: "", firstName: "", email: "", insurance: "" },
  skippedValidation: { queueId: false, personalNumber: false, lastName: false, firstName: false, email: false, insurance: false },
};

const defaultWelcomePageState = {
  terminalUID: "",
  mobileUID: "",
  mobileAppTitle: "",
  mobileAppSubTitle: "",
  isMobileAppRegistrationEnabled: false,
  isClosed: false,
  closedTitle: "",
  closedSubTitle: "",
  keyNumber: "",
  isPersonalNumberValidationSkipEnabled: false,
  isDrivingLicenceEnabled: false,
  isIDCardEnabled: false,
  isWithoutCardEnabled: false,
  cameraImageWidth: 0,
  cameraImageHeight: 0,
  cameraPreviewQuality: 0,
  cameraImageBrightnessF: 0.0,
  cameraImageRotation: 0,
  cameraImageCropX: 0.0,
  cameraImageCropY: 0.0,
  cameraImageCropPercentX: 0.0,
  cameraImageCropPercentY: 0.0,
  testPhotoCapture: false,
  isErrorPageEnabled: false,
  isServicePageEnabled: false,
  isScreenSaverEnabled: false,
  screenSaverHourStart: 0,
  screenSaverHourEnd: 0,
  screenSaverMinuteStart: 0,
  screenSaverMinuteEnd: 0,
  isSoundEnabled: false,
  isCursorEnabled: false,
  isBrightnessAdjustmentEnabled: false,
  brightnessMin: 0,
  brightnessMax: 0,
  isPostTestEnabled: false,
  isScreenResolutionCheckEnabled: false,
  errorMaxRestartCount: 0,
  screenHeight: 0,
  screenWidth: 0,
};

const defaultInfoPanelState = {
  blinkCount: 0,
  blinkDelay: 0,
  callInMessage: "",
  callInPanelMessage: "",
  digitalRemoveBlackBar: false,
  doNotEntryMessage: "",
  doNotEntryPanelMessage: "",
  groupedPanelWeightSum: 0,
  hideClock: false,
  itemPageOffset: 0,
  itemsOnPage: 0,
  nameFormat: 0,
  ordinations: null,
  rootLayoutMarginBottom: 0,
  rootLayoutMarginLeft: 0,
  rootLayoutMarginRight: 0,
  rootLayoutMarginTop: 0,
  showInQueueTime: false,
  showPositionNumbers: false,
  type: 0,
  twoColumnsEnabled: false,
  waitForCallPanelMessage: "",
  topRightLabel: "",
  topLeftLabel: "",
  hideTopLeftLabel: false,
  hideTopRightLabel: false,
  showTopLeftLabelOnAllStates: false,
  waitingLabel: "",
  hideWaitingLabel: false,
};

const defaultNavigateOptions = {
  replace: true,
};

export function FormProvider({ children }) {
  const isTerminal = Const.isTerminal();
  const { isConnected, messages, publish, lastMessage } = useMqttClient(Const.TERMINAL_APP_SERVICE_WEB_SOCKET_URL, Const.TOPIC_MQTTT_LOCAL);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { instance } = useAxios(Const.API_URL);
  const { instance: axiosTerminalAppInstance } = useAxios(Const.TERMINAL_APP_URL);
  const navigate = useNavigate();

  const [logText, setLogText] = useState("");

  const [isTaskRunning, setTaskRunning] = useState(false);
  const [isDebugMode, setDebugMode] = useState(false);
  const [isDebugModeBackBtnClickable, setIsDebugModeBackBtnClickable] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pageRendered, setPageRendered] = useState(false);
  const [state, setState] = useState(defaultState);
  const [isInfoPanel, setIsInfoPanel] = useState(false);

  //slouzi pro nastaveni dark light theme musi se alespon volat i kdy handlovani toggleTheme je v DebugPage
  const [isDarkMode, toggleTheme, setThemeByValue] = useDarkMode();
  const [isInErrorState, setErrorState] = useErrorState();
  const windowSize = useWindowSize();

  const [storageStartTime, setStorageStartTime, removeStorageStartTime] = useSessionStorage(Const.SS_START_TIME, 0);
  const [storageUid, setStorageUid, removeStorageUid] = useStorage(Const.SS_UID, "", isTerminal ? window.localStorage : window.sessionStorage);
  const [storageRegType, setStorageRegType, removeStorageRegType] = useStorage(Const.SS_REG_TYPE, "", isTerminal ? window.localStorage : window.sessionStorage);
  const [firstName, setFirstName, removeFirstName] = useLocalStorage(Const.LS_FIRST_NAME, "");
  const [lastName, setLastName, removeLastName] = useLocalStorage(Const.LS_LAST_NAME, "");
  const [personalNumber, setPersonalNumber, removePersonalNumber] = useLocalStorage(Const.LS_PERSONAL_NUMBER, "");
  const [insurance, setInsurance, removeInsurance] = useLocalStorage(Const.LS_INSURANCE, "");
  const [email, setEmail, removeEmail] = useLocalStorage(Const.LS_EMAIL, "");

  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [terminalSettingsState, setTerminalState] = useState(defaultWelcomePageState);
  const [infoPanelState, setInfoPanelState] = useState(defaultInfoPanelState);
  const [currentBtnPageIndex, setCurrentBtnPageIndex] = useState(0);
  const [selectedButtonId, setSelectedButtonId] = useState(0);
  const [lastSelectedButtonId, setLastSelectedButtonId] = useState(0);
  const [qTitle, setQTitle] = useState("");
  const [pageData, setPageData] = useState(isTerminal ? registrationDataRpi : registrationData);
  const [buttonsData, setButtonsData] = useState(null);
  const [lastError, setLastError] = useState(0);
  const [lastProcessedMqttMessage, setLastProcessedMqttMessage] = useState(null);

  //const [errorRestartCount, setErrorRestartCount, removeErrorRestartCount] = useLocalDb(Const.LS_ERROR_RESTART_COUNT, 0);
  const [isAnimationDisabled, setAnimationDisabled, removeAnimationDisabled] = useLocalStorage(Const.LS_ANIMATION_DISABLED, false);
  const [isSoundEnabled, setSoundEnabled, removeSoundEnabled] = useLocalStorage(Const.LS_SOUND_ENABLED, false);
  const [isCursorEnabled, setCursorEnabled, removeCursorEnabled] = useLocalStorage(Const.LS_CURSOR_ENABLED, false);
  const [isWithoutCardEnabled, setWithoutCardEnabled, removeWithoutCardEnabled] = useLocalStorage(Const.LS_WITHOUT_CARD_ENABLED, false);
  const [isPhotoCaptureTestEnabled, setPhotoCaptureTestEnabled, rempvePhotoCaptureTestEnabled] = useLocalStorage(Const.LS_PHOTO_CAPTURE_TEST_ENABLED, false);

  const { clear: clearTimer, reset: resetTimer } = useTimeout(() => navigateAfterTimeout(), Const.PAGE_EXPIRED_TIME);

  //na produkci vzdy je timer povolen
  let isTimersEnabled = Const.isProduction() || Const.isPreproduction();

  //handlovani animaci
  setAnimationStateDisabled(isAnimationDisabled);

  //v debug zakaz timer nebo si ho povol na test
  if (Const.isDebugMode()) {
    isTimersEnabled = false;
  }

  //nenili timer povolen smaz ho napr v debug
  if (!isTimersEnabled) {
    clearTimer();
  }

  const startTime = performance.now();

  useEffect(() => {
    async function scopedLoadData() {
      postPoneExpiredInterval();

      setLoaded(false);
      setPageRendered(false);
      setRegType();
      setPatientFromLocalStorage();

      let localIsInfoPanel = false;
      const result = await populateData(getUid());
      //console.log(result);

      if (result) {
        const pages = result.pages;
        console.log(result);

        if (isTerminal) {
          //zjisit zda se jedna o info panel
          if (result?.deviceMode === Const.MODE_INFO_PANEL) {
            setIsInfoPanel(true);
            localIsInfoPanel = true;
          }

          //nacist dark mode z jsonu na serveru
          //aby to fungovalo musi byt delay
          setTimeout(() => {
            setThemeByValue(result.enableDarkTheme);
            //logd("enableDarkTheme:" + result.enableDarkTheme);
          }, 500);

          //nastaveni photo option pro RPI OS
          if (!localIsInfoPanel) {
            await setCameraOptions(result);
          }

          //nastaveni screen brightness po nacteni dat z API na MAX pak se to bude ridit dle screensaveru
          if (!localIsInfoPanel) {
            // || Const.isDebugMode()) {
            const max = result?.isBrightnessAdjustmentEnabled ? result.brightnessMax : 255; //nejvyssi hodnota v kodu se rozhodne dle displeje zda bude 100 nebo 255
            logd(`Nastavuji setBrightnessTo po startu na MAX s hodnotou ${max}, isBrightnessAdjustmentEnabled: ${result?.isBrightnessAdjustmentEnabled}`);

            await setBrightnessTo(Const.STATE_MAX, max, 0);
          }
        }

        let terminalState = terminalSettingsState;

        terminalState.terminalUID = result.terminalUid;
        terminalState.isClosed = result.isClosed;
        terminalState.mobileAppSubTitle = result.mobileAppSubTitle;
        terminalState.mobileAppTitle = result.mobileAppTitle;
        terminalState.isMobileAppRegistrationEnabled = result.isMobileAppRegistrationEnabled;
        terminalState.closedTitle = result.closedPageTitle;
        terminalState.closedSubTitle = result.closedPageText;
        terminalState.keyNumber = result.keyNumber;
        terminalState.isPersonalNumberValidationSkipEnabled = result.isPersonalNumberValidationSkipEnabled;

        terminalState.isWithoutCardEnabled = result.isWithoutCardEnabled;
        if (isWithoutCardEnabled != terminalState.isWithoutCardEnabled) {
          setWithoutCardEnabled(terminalState.isWithoutCardEnabled);
        }

        terminalSettingsState.isSoundEnabled = result.enableSound;
        if (isSoundEnabled != terminalState.isSoundEnabled) {
          setSoundEnabled(terminalState.isSoundEnabled);
        }

        terminalState.isCursorEnabled = result.isCursorEnabled;
        if (isCursorEnabled != terminalState.isCursorEnabled) {
          setCursorEnabled(terminalState.isCursorEnabled);
        }

        //other doc registration
        terminalState.isIDCardEnabled = result.isIDCardEnabled;
        terminalState.isDrivingLicenceEnabled = result.isDrivingLicenceEnabled;

        //camera test
        terminalState.testPhotoCapture = result.testPhotoCapture;
        if (isPhotoCaptureTestEnabled != terminalState.testPhotoCapture) {
          setPhotoCaptureTestEnabled(terminalState.testPhotoCapture);
        }

        terminalState.cameraImageWidth = result.cameraImageWidth;
        terminalState.cameraImageHeight = result.cameraImageHeight;
        terminalState.cameraPreviewQuality = result.cameraPreviewQuality;
        terminalState.cameraImageBrightnessF = result.cameraImageBrightnessF;
        terminalState.cameraImageRotation = result.cameraImageRotation;
        terminalState.cameraImageCropX = result.cameraImageCropX;
        terminalState.cameraImageCropY = result.cameraImageCropY;
        terminalState.cameraImageCropPercentX = result.cameraImageCropPercentX;
        terminalState.cameraImageCropPercentY = result.cameraImageCropPercentY;

        //scrensaver
        terminalState.isScreenSaverEnabled = result.enableScreenSaver;
        terminalState.screenSaverHourStart = result.screenSaverHourStart;
        terminalState.screenSaverHourEnd = result.screenSaverHourEnd;
        terminalState.screenSaverMinuteStart = result.screenSaverMinuteStart;
        terminalState.screenSaverMinuteEnd = result.screenSaverMinuteEnd;

        //screen brightness
        terminalState.isBrightnessAdjustmentEnabled = result.isBrightnessAdjustmentEnabled;
        terminalState.brightnessMax = result.brightnessMax;
        terminalState.brightnessMin = result.brightnessMin;

        //post testy kontrola rozliseni
        terminalState.isPostTestEnabled = result.isPostTestEnabled;
        terminalState.isScreenResolutionCheckEnabled = result.isScreenResolutionCheckEnabled;
        terminalState.errorMaxRestartCount = result.cameraProblemRestartMaxCount;
        terminalState.screenWidth = result.screenWidth;
        terminalState.screenHeight = result.screenHeight;

        terminalState.mobileUID = getUid();

        setTerminalState(terminalState);

        setButtonsData(pages);

        if (isTerminal) {
          injectApiDataToPages(pages);
        }

        //add info panel state
        if (localIsInfoPanel) {
          //  console.log("xxxx", result?.infoPanel);

          let panelState = infoPanelState;
          panelState.blinkCount = result?.infoPanel?.blinkCount;
          panelState.blinkDelay = result?.infoPanel?.blinkDelay;
          panelState.callInMessage = result?.infoPanel?.callInMessage;
          panelState.callInPanelMessage = result?.infoPanel?.callInPanelMessage;
          panelState.doNotEntryMessage = result?.infoPanel?.doNotEntryMessage;
          panelState.doNotEntryPanelMessage = result?.infoPanel?.doNotEntryPanelMessage;
          panelState.groupedPanelWeightSum = result?.infoPanel?.groupedPanelWeightSum;
          panelState.hideClock = result?.infoPanel?.hideClock;
          panelState.itemPageOffset = result?.infoPanel?.itemPageOffset;
          panelState.itemsOnPage = result?.infoPanel?.itemsOnPage;
          panelState.nameFormat = result?.infoPanel?.nameFormat;
          panelState.ordinations = result?.infoPanel?.ordinations;
          panelState.rootLayoutMarginBottom = result?.infoPanel?.rootLayoutMarginBottom;
          panelState.rootLayoutMarginLeft = result?.infoPanel?.rootLayoutMarginLeft;
          panelState.rootLayoutMarginRight = result?.infoPanel?.rootLayoutMarginRight;
          panelState.rootLayoutMarginTop = result?.infoPanel?.rootLayoutMarginTop;
          panelState.showInQueueTime = result?.infoPanel?.showInQueueTime;
          panelState.showPositionNumbers = result?.infoPanel?.showPositionNumbers;
          panelState.twoColumnsEnabled = result?.infoPanel?.twoColumnsEnabled;
          panelState.type = result?.infoPanel?.type;
          panelState.waitForCallPanelMessage = result?.infoPanel?.waitForCallPanelMessage;

          panelState.topRightLabel = result?.infoPanel?.topRightLabel;
          panelState.topLeftLabel = result?.infoPanel?.topLeftLabel;
          panelState.hideTopLeftLabel = result?.infoPanel?.hideTopLeftLabel;
          panelState.hideTopRightLabel = result?.infoPanel?.hideTopRightLabel;
          panelState.showTopLeftLabelOnAllStates = result?.infoPanel?.showTopLeftLabelOnAllStates;
          panelState.hideWaitingLabel = result?.infoPanel?.hideWaitingLabel;
          panelState.waitingLabel = result?.infoPanel?.waitingLabel;

          setInfoPanelState(panelState);
        }

        handleSplashClose();

        setTimeout(() => {
          checkScreenResolution(localIsInfoPanel, terminalState);
        }, 1000);
      }
    }
    scopedLoadData();
  }, []);

  useEffect(() => {
    if (lastMessage) {
      //zavolam to abych stanovil state
      if (isTerminal) {
        handleMqttMessage(lastMessage);
      }
    }
  }, [lastMessage]);

  async function handleMqttMessage(newMessage) {
    //nezpracovavat stejnou message vicekrat
    if (lastProcessedMqttMessage !== newMessage) {
      setLastProcessedMqttMessage(newMessage);
      //logd(new Date().toISOString(), "handleMqttMessage", newMessage, state);

      // Speciální logika pro téma RPi
      if (newMessage?.topic === Const.TOPIC_MQTTT_LOCAL) {
        if (newMessage.payload === Const.LOCAL_MESSAGE_TERMINAL_CLOSED) {
          logd("Terminál closed");
          setTerminalState((prevState) => ({
            ...prevState,
            isClosed: true,
          }));

          //kdykoliv v ramci push notifikace muze prijit terminal close
          // v pripade ze budu na jine nez insert card page
          // tak pacient dojde na konec kolecka inser, buttons, remove card a ve welcome page se nastavi terminal closed
          //problem bude kdyz bude na page_card_insert a nebude vlozen prukaz tak ho presmeruji na home
          handleIsTerminalClosed(true);

          //logd("terminal data loaded", result);
        } else if (newMessage.payload === Const.LOCAL_MESSAGE_TERMINAL_OPEN) {
          logd("Terminál opened");
          setTerminalState((prevState) => ({
            ...prevState,
            isClosed: false,
          }));
        } else if (newMessage.payload === Const.LOCAL_MESSAGE_TERMINAL_ERROR_PAGE_ENABLED) {
          logd("Terminál error page enabled");
          setTerminalState((prevState) => ({
            ...prevState,
            isErrorPageEnabled: true,
          }));

          //kdykoliv v ramci push notifikace muze prijit terminal error page enabled
          // v pripade ze budu na jine nez insert card page
          // tak pacient dojde na konec kolecka inser, buttons, remove card a ve welcome page se nastavi terminal error page enabled
          //problem bude kdyz bude na page_card_insert a nebude vlozen prukaz tak ho presmeruji na home
          handleIsTerminalClosed(true);

          //logd("terminal data loaded", result);
        } else if (newMessage.payload === Const.LOCAL_MESSAGE_TERMINAL_ERROR_PAGE_DISBLED) {
          logd("Terminál error page disabled");
          setTerminalState((prevState) => ({
            ...prevState,
            isErrorPageEnabled: false,
          }));
        } else if (newMessage.payload === Const.LOCAL_MESSAGE_TERMINAL_SERVICE_PAGE_ENABLED) {
          logd("Terminál service page enabled");
          setTerminalState((prevState) => ({
            ...prevState,
            isServicePageEnabled: true,
          }));

          //kdykoliv v ramci push notifikace muze prijit terminal service page enabled
          // v pripade ze budu na jine nez insert card page
          // tak pacient dojde na konec kolecka inser, buttons, remove card a ve welcome page se nastavi terminal service page enabled
          //problem bude kdyz bude na page_card_insert a nebude vlozen prukaz tak ho presmeruji na home
          handleIsTerminalClosed(true);

          //logd("terminal data loaded", result);
        } else if (newMessage.payload === Const.LOCAL_MESSAGE_TERMINAL_SERVICE_PAGE_DISBLED) {
          logd("Terminál service page disabled");
          setTerminalState((prevState) => ({
            ...prevState,
            isServicePageEnabled: false,
          }));
        } else if (newMessage.payload === Const.LOCAL_MESSAGE_TERMINAL_SEND_UI_LOG_TO_API) {
          logd("Terminál Send UI log to API");
          await sendLogToApi();
        }
      }
    }
  }

  const isScreenOff = useDetectScreenOff();
  if (pageRendered) {
    //logd("pageRendered true call handleIntervalAfterScreenOn");
    handleIntervalAfterScreenOn();
  }

  async function populateData(uid) {
    try {
      let { data: item } = await TerminalService.getData(instance, uid);
      logd("populateData - data populated");

      return item;
    } catch (ex) {
      loge(ex, ex?.response);
      setLastError(ex?.response?.status);
      if (ex?.response?.status === 400) {
        navigateToErrorPage(Const.PAGE_ERROR_NOT_FOUND);
      }
    }
  }

  async function setDarkMode(isDark) {
    try {
      setTaskRunning(true);
      await TerminalService.setDarkMode(instance, getUid(), isDark);
      logd("setDarkMode isDark:" + isDark);
    } catch (ex) {
      loge(ex, ex?.response);
      setLastError(ex?.response?.status);
      if (ex?.response?.status === 400) {
        navigateToErrorPage(Const.PAGE_ERROR_NOT_FOUND);
      } else {
        navigateToErrorPage(Const.PAGE_ERROR);
      }
    } finally {
      setTaskRunning(false);
    }
  }

  async function setWithouCardApi(state) {
    try {
      setTaskRunning(true);
      setWithoutCardEnabled(state);
      await TerminalService.setWithoutCard(instance, getUid(), state);
      logd("setWithouCardApi state:" + state);
    } catch (ex) {
      loge(ex, ex?.response);
      setLastError(ex?.response?.status);
      if (ex?.response?.status === 400) {
        navigateToErrorPage(Const.PAGE_ERROR_NOT_FOUND);
      } else {
        navigateToErrorPage(Const.PAGE_ERROR);
      }
    } finally {
      setTaskRunning(false);
    }
  }

  async function setSoundEnabledApi(state) {
    try {
      setTaskRunning(true);
      setSoundEnabled(state);
      await TerminalService.setSoundEnabled(instance, getUid(), state);
      logd("setSoundEnabledApi state:" + state);
    } catch (ex) {
      loge(ex, ex?.response);
      setLastError(ex?.response?.status);
      if (ex?.response?.status === 400) {
        navigateToErrorPage(Const.PAGE_ERROR_NOT_FOUND);
      } else {
        navigateToErrorPage(Const.PAGE_ERROR);
      }
    } finally {
      setTaskRunning(false);
    }
  }

  async function setCursorEnabledApi(state) {
    try {
      setTaskRunning(true);
      setCursorEnabled(state);
      await TerminalService.setCursorEnabled(instance, getUid(), state);
      logd("setCursorEnabledApi state:" + state);
    } catch (ex) {
      loge(ex, ex?.response);
      setLastError(ex?.response?.status);
      if (ex?.response?.status === 400) {
        navigateToErrorPage(Const.PAGE_ERROR_NOT_FOUND);
      } else {
        navigateToErrorPage(Const.PAGE_ERROR);
      }
    } finally {
      setTaskRunning(false);
    }
  }

  async function setPhotoCaptureTestEnabledApi(state) {
    try {
      setTaskRunning(true);
      setPhotoCaptureTestEnabled(state);
      await TerminalService.setPhotoCaptureTest(instance, getUid(), state);
      logd("setPhotoCaptureTestEnabledApi state:" + state);
    } catch (ex) {
      loge(ex, ex?.response);
      setLastError(ex?.response?.status);
      if (ex?.response?.status === 400) {
        navigateToErrorPage(Const.PAGE_ERROR_NOT_FOUND);
      } else {
        navigateToErrorPage(Const.PAGE_ERROR);
      }
    } finally {
      setTaskRunning(false);
    }
  }

  async function getErrotRestartCountVal() {
    return await localforage.getItem(Const.LS_ERROR_RESTART_COUNT);
  }

  async function setErrorRestartCount(val) {
    await localforage.setItem(Const.LS_ERROR_RESTART_COUNT, val);
  }
  //vkladat sem pripadne zavisle citace kter jsou pri beznem restartu dobre smazat
  function handleResetDependentCounters() {
    logd("handleResetDependentCounters() reset čítačů při volání runRestartOrShutdown() z aplikace");
    setErrorRestartCount(0);
  }

  async function runRestartOrShutdown(operation, resetDependentCounters = true) {
    try {
      setTaskRunning(true);

      if (resetDependentCounters) {
        handleResetDependentCounters();
      }

      if (isTerminal) {
        if (!Const.isDebugMode()) {
          logd("runRestartOrShutdown operation:" + operation);
          await TerminalAppService.runRestartOrShutdown(axiosTerminalAppInstance, operation);
        } else {
          logd("fake runRestartOrShutdown operation:" + operation);
        }
      }
    } catch (ex) {
      loge(ex, ex?.response);
      setLastError(ex?.response?.status);
      if (ex?.response?.status === 400) {
        navigateToErrorPage(Const.PAGE_ERROR_NOT_FOUND);
      } else {
        navigateToErrorPage(Const.PAGE_ERROR);
      }
    } finally {
      setTaskRunning(false);
    }
  }

  async function eraseTerminalLog() {
    try {
      setTaskRunning(true);
      if (isTerminal) {
        logd("eraseTerminalLog");
        await TerminalAppService.eraseTerminalLog(axiosTerminalAppInstance);
      }
    } catch (ex) {
      loge(ex, ex?.response);
      setLastError(ex?.response?.status);
      if (ex?.response?.status === 400) {
        navigateToErrorPage(Const.PAGE_ERROR_NOT_FOUND);
      } else {
        navigateToErrorPage(Const.PAGE_ERROR);
      }
    } finally {
      setTaskRunning(false);
    }
  }

  async function getTerminalLog() {
    try {
      setTaskRunning(true);
      if (isTerminal) {
        logd("getTerminalLog");
        const result = await TerminalAppService.getTerminalLog(axiosTerminalAppInstance);
        return result?.data;
      }
    } catch (ex) {
      loge(ex, ex?.response);
    } finally {
      setTaskRunning(false);
    }
  }

  async function getTerminalHwInfo() {
    try {
      setTaskRunning(true);
      if (isTerminal) {
        logd("getTerminalHwInfo");
        const result = await TerminalAppService.getTerminalHwInfo(axiosTerminalAppInstance);
        return result?.data;
      }
    } catch (ex) {
      loge(ex, ex?.response);
    } finally {
      setTaskRunning(false);
    }
  }

  async function validate(index, localState = null) {
    const page = getPageData(index);
    logd("validate", page, state);

    const isTerminal = Const.isTerminal();
    if (!isTerminal && index === Const.REG_02_INDEX) {
      await registerPatientOnMobile(page);
    } else if (isTerminal && index === Const.REG_RPI_REASON_BUTTON_INDEX) {
      await putPatientQueue(page, localState);
    } else {
      navigateNext(page.nextRoute);
    }
  }

  async function putPatientQueue(page, localState) {
    logd("putPatientQueue", localState);
    if (VS.handleSubmit(null, localState, validateFormItem, null)) {
      try {
        setTaskRunning(true);
        //savePatientToLocalStorage(state);
        navigateNext(page.nextRoute);
        await PatientService.putPatientQueue(instance, localState.pid, getUid(), localState.queueId, localState.isSetByDefault);
        // await PatientService.x(instance, req);
      } catch (ex) {
        loge(ex, ex?.response);
        setLastError(ex?.response?.status);
      } finally {
        setTaskRunning(false);
      }
    }
  }

  async function registerPatientOnMobile(page) {
    if (VS.handleSubmit(null, state, validateFormItem, null)) {
      try {
        setTaskRunning(true);
        savePatientToLocalStorage(state);
        navigateNext(page.nextRoute);
        await PatientService.register(instance, getUid(), state.queueId, getRegType(), setViewToModel(state));
        // await PatientService.x(instance, req);
      } catch (ex) {
        loge(ex, ex?.response);
        setLastError(ex?.response?.status);
        if (ex?.response?.status === 400) {
          navigateToErrorPage(Const.PAGE_ERROR_NOT_FOUND);
        } else if (ex?.response?.status !== 409) {
          navigateToErrorPage(Const.PAGE_ERROR);
        }
      } finally {
        setTaskRunning(false);
      }
    }
  }

  async function sendLogToApi() {
    logd("sendLogToApi");

    const builId = process.env.REACT_APP_BUILD_ID ?? "";

    const lines = [];
    lines.push("------------------ Web REACT JS app log ------------------");
    lines.push(`Terminál: ${terminalSettingsState.terminalUID}`);
    lines.push(`Mobile UID: ${getUid()}`);
    lines.push(`Verze scriptu: ${process.env.REACT_APP_VERSION}.${builId}`);
    lines.push(`Enviroment: ${Const.ENV}`);
    lines.push(`React JS Web App URL: ${window?.location?.origin}`);
    lines.push(`API URL: ${Const.API_URL}`);
    lines.push(`Python TERMINAL_APP URL: ${Const.TERMINAL_APP_URL}`);
    lines.push("");
    lines.push("---------------------------------------");
    //pridati aktualniho stavu aplikace
    lines.push(`${logText}[${formatTime()}] APP STATE: ${formatLine(state)}`);

    const dataToSend = lines.join("\n");

    // await new Promise((resolve) =>
    //   setTimeout(async () => {
    let isOk = false;
    if (isTerminal) {
      try {
        setTaskRunning(true);
        await TerminalService.addLog(instance, getUid(), dataToSend);
        isOk = true;
      } catch (ex) {
        loge(ex, ex?.response);
      } finally {
        setTaskRunning(false);
        if (isOk) {
          logd(`Data úspěšně odeslána do API, log bude smazán za ${Const.LOG_DELETE_INTERVAL / 1000}s`);

          setTimeout(() => {
            setLogText("");
          }, Const.LOG_DELETE_INTERVAL);
        }
      }
    }
  }

  function handleIsTerminalClosed(isClosed) {
    //jsem li na terminalu a jsem na strance REG_RPI_INSERT_CARD_INDEX a prijde push message termnal_closed
    //tak nebudu cekat na kolecko card inser - button -remove card

    //logd(isTerminal, isClosed, path, Const.getIndexForPage(path));
    if (isTerminal && isClosed && Const.getIndexForPage(location.pathname) === Const.REG_RPI_INSERT_CARD_INDEX) {
      logd("Přesměrování na home po close terminal notification");

      navigateHome();
    }
  }

  function handleDebug() {
    //neni li jeste debug mode a meni se na true tak pockej 500 ms na zabraneni kliknuti na talcitko zpet ihned po zobrazeni debug talcitka = debouncing
    if (!isDebugMode) {
      //logd("settimout");
      setTimeout(() => {
        setIsDebugModeBackBtnClickable(true);
        //logd("end");
      }, 500);
    } else {
      setIsDebugModeBackBtnClickable(false);
    }
    setDebugMode(!isDebugMode);
  }

  function postPoneExpiredInterval() {
    setStorageStartTime(Date.now());
  }

  function handleIntervalAfterScreenOn() {
    //tato polozka jen kdy se jedna o telefon ne web terminal

    if (!isTerminal) {
      if (isTimersEnabled || Const.isProduction()) {
        if (!isScreenOff && storageStartTime > 0) {
          const timeElapsed = Date.now() - storageStartTime;
          const isExpired = timeElapsed > Const.PAGE_EXPIRED_TIME ? true : false;
          //musi byt console.log jinak hazi chybu protoze se jeste nevytvoril cely objekt
          //console.log("chekcInterval: isExpired:" + isExpired + " timeElapsed:" + timeElapsed / 1000 + " interval:" + Const.PAGE_EXPIRED_TIME);

          if (isExpired && storageStartTime > 0) {
            setPageRendered(false);
            //musim pouzit debounce 500ms aby se vse stihlo bez chyby
            setTimeout(() => {
              logd("chekcInterval is expired navigate out");
              navigateAfterTimeout();
            }, 500);
          }
        }
      }
    }
  }

  function navigateAfterTimeout() {
    if (!isTerminal) {
      navigateToErrorPage(Const.PAGE_ERROR_PAGE_EXPIRED);
    }
  }

  //spusti kod napriklad smazani dilogu, toastu pri prechodu na dalsi stranku
  function runBeforeNavigate(params) {
    if (isTerminal) {
      toast.dismissAll();
    }
  }
  //naviguje na test page nebo camera page z debug page a resi vsechny timery
  function navigateToServicePage(route) {
    if (isTerminal) {
      runBeforeNavigate();
      navigate(route, defaultNavigateOptions);
    }
  }

  function navigateToErrorPage(page) {
    clean();
    navigate(page, defaultNavigateOptions);
  }

  function clean() {
    clearTimer();
    runBeforeNavigate();
    setLastError("");
    if (!isTerminal) {
      removeStorageUid();
      removeStorageRegType();
    }
  }

  function resetToNextPatient() {
    if (isTerminal) {
      setState(defaultState);
    }
  }

  function getUid() {
    //debugger;
    let uid;
    try {
      //nejdriv se podivam do ulr zda tam neni uid
      uid = searchParams.get("uid");
      //  logd(uid, storageUid);

      //kdyz neni prazdne tak ho ulozim
      if (!VS.isEmpty(uid)) {
        //logd("ukladam uid: " + uid);
        setStorageUid(Helper.encrypt(uid));
      }

      //v pripade ze uzivatel refreshnul page a ztratili se info o terminalu na jine strance nez home tak nacti z sessionStorage
      // jestli refresnul stranku na home tak ma smulu bez tak na hlavni strance ma searchParams uid
      if (VS.isEmpty(uid) && !VS.isEmpty(storageUid) && location?.pathname !== Const.HOME) {
        uid = Helper.decrypt(storageUid);
        logd("Nacitam UID ze storage", uid);
      }
    } catch (error) {
      loge("Chyba při UID e:" + error);
    }

    return uid;
  }

  function getRegType() {
    let regType;
    try {
      //nejdriv se podivam do ulr zda tam neni uid
      regType = searchParams.get("regType");

      //v pripade ze uzivatel refreshnul page a ztratili se info o terminalu na jine strance nez home tak nacti z sessionStorage
      // jestli refresnul stranku na home tak ma smulu bez tak na hlavni strance ma searchParams uid
      if (VS.isEmpty(regType) && !VS.isEmpty(storageRegType) && location?.pathname !== Const.HOME) {
        regType = Helper.decrypt(storageRegType);
        logd("Nacitam regType ze storage");
      }
    } catch (error) {
      loge("Chyba při getRegType e:" + error);
    }

    return regType;
  }

  function setRegType() {
    let regType;
    try {
      //nejdriv se podivam do ulr zda tam neni uid
      regType = searchParams.get("regType");

      //kdyz neni prazdne tak ho ulozim
      if (!VS.isEmpty(regType)) {
        setStorageRegType(Helper.encrypt(regType));
      }
    } catch (error) {
      loge("Chyba při setRegType e:" + error);
    }

    return regType;
  }

  function savePatientToLocalStorage(item) {
    try {
      if (item && !isTerminal) {
        setLastName(Helper.encrypt(item.lastName));
        setFirstName(Helper.encrypt(item.firstName));
        setInsurance(Helper.encrypt(item.insurance));
        setEmail(Helper.encrypt(item.email));
        setPersonalNumber(Helper.encrypt(item.personalNumber));
      }
    } catch (error) {
      loge("Chyba při ukládání dat pacienta do local storage e:" + error);
    }
  }

  function setPatientFromLocalStorage() {
    try {
      if (defaultState && !isTerminal) {
        defaultState.lastName = Helper.decrypt(lastName);
        defaultState.firstName = Helper.decrypt(firstName);
        defaultState.insurance = Helper.decrypt(insurance);
        defaultState.email = Helper.decrypt(email);
        defaultState.personalNumber = Helper.decrypt(personalNumber);
        setState(defaultState);
      }
    } catch (error) {
      loge("Chyba při získávání dat pacienta z local storage e:" + error);
    }
  }

  function setViewToModel(data) {
    let personalNumber = data.personalNumber;
    //jeli skipnuta validace RC musi se nastavit unikatni identifikator aby jich na serveru mohlo byt vice
    if (data?.skippedValidation["personalNumber"] ?? false) {
      personalNumber = Helper.getRandomUID(7, true);
    }
    let vm = {
      name: data?.firstName?.toUpperCase(),
      surname: data?.lastName?.toUpperCase(),
      insuranceCompany: data.insurance,
      personalID: personalNumber?.toUpperCase(),
      email: data.email,
    };

    return vm;
  }

  function validateFormItem(name, value, skipValidation = false) {
    let errors = state.errors;
    let skippedValidation = state.skippedValidation;
    //debugger;
    //let localState = { ...state };

    if (isTerminal) {
      switch (name) {
        case "pid":
          errors[name] = VS.requiredNotZero(value, "PatientID");
          break;
        case "queueId":
          errors[name] = VS.requiredNotZero(value, "Důvod návštěvy");
          break;
      }
    } else {
      switch (name) {
        case "lastName":
          errors[name] = VS.required(value, "Příjmení");
          break;
        case "firstName":
          errors[name] = VS.required(value, "Jméno");
          break;
        case "insurance":
          errors[name] = VS.requiredNotZero(value, "Pojišťovna");
          break;
        case "email":
          //kontrola emailu jen kdyz je vyplnen
          if (value) {
            errors[name] = VS.validateEmail(value, "E-mail");
          } else {
            errors[name] = "";
          }
          break;
        case "personalNumber":
          if (value) {
            if (value?.length > 10) {
              value = value.substring(0, 10);
            }
            errors[name] = VS.validatePersonalNumber(value);

            //pro test abych nemusel porad opisovat rc
            if (value === "888") {
              errors[name] = "";
            }
          } else {
            errors[name] = VS.required(value, "Rodné číslo", "");
          }

          //musim vzdy nastavit zda je nebo neni skip do state
          skippedValidation[name] = skipValidation;

          if (skipValidation) {
            errors[name] = "";
            value = "";
          }

          break;
      }
    }
    //logd(value, localState.personalNumber, state.personalNumber);

    //logd(errors);

    //zobrazeni error bg pri chybe
    if (isTerminal) {
      if (isObjEmpty(errors)) {
        setErrorState(false);
      } else {
        setErrorState(true);
      }
    }

    setState({ ...state, errors, skippedValidation, [name]: value });
  }

  function isObjEmpty(obj) {
    let isEmtpy = true;
    for (var prop in obj) {
      const val = obj[prop];
      isEmtpy = VS.isEmpty(val);
      //logd(prop, val, isEmtpy);
      if (!isEmtpy) {
        return false;
      }
    }

    return isEmtpy;
  }

  function handleInput(e) {
    const { name, value } = e.target;
    setData(name, value);
  }

  function handleInputWithKeyboard(name, value) {
    setData(name, value);
  }

  //v pripade ze uzivatel klikne na tlacitko zpet v browseru a pak se vrati muze byt jiz prazdny state v tom pripade je dobre udelat nejakou akci
  function isEmptyState() {
    //staci porovnat tyto hodnoty
    if (state && state.queueId === 0) {
      // && VS.isEmpty(state.lastName)) {

      return true;
    }
    return false;
  }

  function setData(input, value, input2 = null, value2 = null, input3 = null, value3 = null, input4 = null, value4 = null, validateItem = true) {
    if (validateItem) {
      validateFormItem(input, value);
    }

    const localState = { ...state };
    localState[input] = value;
    if (input2 && value2) {
      localState[input2] = value2;
    }

    if (input3 && value3) {
      localState[input3] = value3;
    }

    if (input4 && value4) {
      localState[input4] = value4;
    }

    logd("setData", input, value, input2, value2, localState);

    setState(localState);
    return localState;
  }

  function navigateNext(route) {
    resetTimer();
    postPoneExpiredInterval();
    setDebugMode(false);
    logd("navigate next", selectedPageIndex, Const.getIndexForPage(route), route);

    setSelectedPageIndex(Const.getIndexForPage(route));
    runBeforeNavigate();
    navigate(route, defaultNavigateOptions);
  }

  function navigateNextInsuranceButton(item, index) {
    const page = getPageData(index);
    logd("navigateNextInsuranceButton", item, index);
    setData("insurance", item.value);
    navigateNext(page.nextRoute);
  }

  function navigateNextButtons(item, index, isSetWithoutTouch = false) {
    logd("navigateNextButtons", item, index);
    //debugger;
    //poznamenat pro tlacitko zpet
    if (selectedButtonId >= 0) {
      setLastSelectedButtonId(selectedButtonId);
    }

    if (item) {
      setSelectedButtonId(item.id);
    }

    if (item && item.queueId === 0) {
      const index = currentBtnPageIndex + 1;
      setCurrentBtnPageIndex(index > 2 ? 2 : index);
      setQTitle(getQueueTitle(item.text));
      logd("navigateNextButtons patient click on parent queue ", getQueueTitle(item.text), "id:" + item.id);
    } else {
      logd("navigateNextButtons patient select queue ", getQueueTitle(item.text), "queueId:" + item.queueId);
      const localState = setData(
        "queueFullName",
        getQueueTitle(item.text),
        "queueName",
        item.text,
        "queueId",
        item.queueId,
        "isSetByDefault",
        isSetWithoutTouch,
        false,
      );
      setQTitle("");
      setCurrentBtnPageIndex(0);
      setSelectedButtonId(0);
      validate(index, localState);
    }
  }

  function getQueueTitle(title) {
    if (qTitle) {
      title = qTitle + " > " + title;
    }
    return title;
  }

  function replaceLastQueueTitleSegment() {
    let title = qTitle;
    if (title && title.length > 0) {
      const lastIndex = title.lastIndexOf(" > ");
      if (lastIndex > 0) {
        title = title.substring(0, lastIndex);
      } else {
        title = ""; //prvni segment neobsahuje > tak ze to jen smazu
      }
    }
    return title;
  }

  function navigateBackButtons() {
    const index = currentBtnPageIndex - 1;

    if (lastSelectedButtonId > 0) {
      setSelectedButtonId(lastSelectedButtonId);
    }
    setCurrentBtnPageIndex(index < 0 ? 0 : index);
    setQTitle(replaceLastQueueTitleSegment());
    beep();
  }

  function showBackButton() {
    return currentBtnPageIndex == 0 ? false : true;
  }

  function navigateHome(uid = "") {
    //debugger;
    runBeforeNavigate();
    setLastError(0);
    resetToNextPatient();
    const passedUid = uid;
    const localUid = getUid();
    setSelectedPageIndex(Const.HOME_INDEX);
    logd(passedUid, localUid);

    searchParams.set("uid", !VS.isEmpty(uid) ? uid : getUid());
    setSearchParams(searchParams);
    if (!isTerminal) {
      resetTimer();
      postPoneExpiredInterval();
    }

    navigate(
      {
        pathname: Const.HOME,
        search: searchParams.toString(),
      },
      defaultNavigateOptions,
    );
  }

  function getPageData(index) {
    // logd("getPageData:", pageData);
    return pageData[index];
  }

  function getButtonsData() {
    return buttonsData;
  }

  // function getApiPage(pageId) {
  //   //   console.log("getApiPage", pageId, apiPageData);

  //   return apiPageData ? apiPageData.filter((item) => item.pageId === pageId) : null;
  // }

  function showCustomToast(text, duration = 10000) {
    toast(text, {
      duration: duration,
    });
  }

  function showCustomToastDialog(text) {
    //pro dialog vzdy zavrit stavajici
    toast.dismissAll();
    showCustomToast(text, Infinity);
  }

  async function checkScreenResolution(isInfoPanel, terminalState) {
    const errorRestartCount = await getErrotRestartCountVal();

    const line = `checkInfoPanelScreenResolution() isInfoPanel:${isInfoPanel}, isPostEnabled: ${terminalState.isPostTestEnabled}, isScreenResolutionCheckEnabled: ${terminalState.isScreenResolutionCheckEnabled}, errorMaxRestartCount: ${terminalState.errorMaxRestartCount}, errorRestartCount: ${errorRestartCount} screenWidth: ${terminalState.screenWidth}, windowWidth: ${windowSize.width}, screenHeight: ${terminalState.screenHeight} windowHeight: ${windowSize.height}`;
    logd(line);
    // alert(line);
    //precondition
    if (isInfoPanel && terminalState?.isPostTestEnabled && terminalState.isScreenResolutionCheckEnabled) {
      //vraci to resolution
      if (windowSize?.width > 0 && windowSize?.height > 0 && terminalState?.screenHeight > 0 && terminalState?.screenWidth > 0) {
        //rozliseni nesedi
        if (terminalState.screenHeight != windowSize.height || terminalState.screenWidth != windowSize.width) {
          //zapis neco || 3 znamena jeli to 0, null nebo przadne bere se to za || tedy 3
          const maxCount = terminalState.errorMaxRestartCount || Const.DEFAULT_MAX_ERROR_RESTART_COUNT;

          //jeste je moznost kontroly - restart neprohl vicekrat nez je povoleno
          if (maxCount > errorRestartCount) {
            logd(
              `checkScreenResolution() - maxCount(${maxCount}) > errorRestartCount(${errorRestartCount}) - nastavuji errorRestartCount + 1 a provádím restart zařízení`,
            );
            let newVal = errorRestartCount + 1;
            logd(`checkScreenResolution - newVal:${newVal}`);
            //    setErrorRestartCount(newVal);

            await setErrorRestartCount(newVal);

            showCustomToastDialog(
              Helper.formatString(e_screen_resolution_restart, windowSize.width, windowSize.height, terminalState.screenWidth, terminalState.screenHeight),
            );

            logd(`checkScreenResolution - errorRestartCount:${newVal}`);
            setTimeout(async () => {
              runRestartOrShutdown(Const.OPERATION_RESTART, false);
            }, 5000);
          } else {
            loge(`checkScreenResolution() - rozlišení nesedí, ale počet restartů (${errorRestartCount}) >= maximální počet (${maxCount})`);
            showCustomToastDialog(
              Helper.formatString(e_screen_resolution, windowSize.width, windowSize.height, terminalState.screenWidth, terminalState.screenHeight),
            );
          }
        } else {
          logd("checkScreenResolution() - rozlišení je OK - resetuji errorRestartCount na 0");
          await setErrorRestartCount(0);
        }
      } else {
        logd("checkScreenResolution() - nemohu pokračovat - některá z hodnot rolišení je 0");
      }
    } else {
      logd("checkScreenResolution() - precondition nevyhovují testu rozlišení");
    }
  }

  function handleSplashClose() {
    const loadedTime = end();
    //logd(loadedTime);
    if (loadedTime < Const.SPLASH_TIME) {
      const splashTimeLeft = (Const.SPLASH_TIME - loadedTime) * 1000;
      //logd(splashTimeLeft);

      const timer = setTimeout(() => {
        //logd("end");
        setLoaded(true);
        setPageRendered(true);
      }, splashTimeLeft);

      return () => clearTimeout(timer);
    } else {
      //logd("without timer");
      setLoaded(true);
      setPageRendered(true);
    }
  }

  function end() {
    const endTime = performance.now();
    let timeDiff = endTime - startTime; //in ms
    // strip the ms
    timeDiff /= 1000;

    // get seconds
    const seconds = Math.round(timeDiff);
    //logd(seconds + " seconds");
    return seconds;
  }

  async function beep(count = 1, playWithoutSoudEnabledCheck = false) {
    logd(`beep - count:${count} - isSoundEnabled: ${isSoundEnabled}`);
    //dodelat volani GPIO na FLASK server
    if ((isSoundEnabled && isTerminal) || playWithoutSoudEnabledCheck) {
      try {
        await TerminalAppService.beep(axiosTerminalAppInstance, count);
      } catch (ex) {
        loge(ex, ex?.response);
        setLastError(ex?.response?.status);
        if (ex?.response?.status === 400) {
          navigateToErrorPage(Const.PAGE_ERROR_NOT_FOUND);
        }
      } finally {
        //setTaskRunning(false);
      }
    }
  }

  async function playGong(playWithoutSoudEnabledCheck = false) {
    logd(`playing gong - isSoundEnabled: ${isSoundEnabled}`);
    if ((isSoundEnabled && isTerminal) || playWithoutSoudEnabledCheck) {
      try {
        const audio = new Audio(Const.GONG_SOUND);
        audio.volume = 1.0; // Rozmezí 0.0 až 1.0
        await audio.play();
      } catch (ex) {
        loge(`Chyba při přehrávání gongu: ${ex}`);
      }
    }
  }

  async function setLedTo(state) {
    //console.log("card reader");
    try {
      let { data } = await TerminalAppService.setLedTo(axiosTerminalAppInstance, state);
      if (!data.isError) {
        console.log("LED is set to:", state);
      } else {
        console.log("Chyba při nastaveni LED", data.message);
      }
    } catch (ex) {
      console.log("Chyba při nastaveni LED", ex);
    }
  }

  async function setBrightnessTo(state, max, min) {
    //console.log("card reader");
    try {
      let { data } = await TerminalAppService.setBrightness(axiosTerminalAppInstance, state, max, min);
      if (!data.isError) {
        let stateVal = "";
        if (state === Const.STATE_MAX) {
          stateVal = "MAX";
        } else if (state === Const.STATE_MIN) {
          stateVal = "MIN";
        }
        console.log("Jas nastaven na stav:", stateVal);
      } else {
        console.log("Chyba při nastaveni jasu", data.message);
      }
    } catch (ex) {
      console.log("Chyba při nastaveni jasu", ex);
    }
  }

  async function setCameraOptions(apiData) {
    try {
      //console.log("xxxx", apiData);

      const options = {
        w: apiData.cameraImageWidth,
        h: apiData.cameraImageHeight,
        q: apiData.cameraPreviewQuality,
        r: apiData.cameraImageRotation,
        b: apiData.cameraImageBrightnessF,
        x: apiData.cameraImageCropX,
        y: apiData.cameraImageCropY,
        sx: apiData.cameraImageCropPercentX,
        sy: apiData.cameraImageCropPercentY,
      };
      let { data } = await TerminalAppService.setCameraOptions(axiosTerminalAppInstance, options);
      if (!data.isError) {
        logd("Nastavení parametrů kamery do Rpi OS - ok");
      } else {
        const e = "Chyba při nastavení parametrů kamery do Rpi OS";
        loge(e, data?.message);
      }
    } catch (ex) {
      const e = "Chyba při nastavení parametrů kamery do Rpi OS";
      loge(e, ex);
    }
  }

  function handleAnimationDisabled(state) {
    logd("isNowDisabled:" + isAnimationDisabled);
    setAnimationDisabled(state);
    setAnimationStateDisabled(state);
    logd("after change Disabled:" + MotionGlobalConfig.skipAnimations);
  }

  function setAnimationStateDisabled(state) {
    //disable motion globally
    MotionGlobalConfig.skipAnimations = state;
  }

  //prepise data z prichoziho jsonu TerminalSettings do dat apliakce - aktualne titulky a podtitulky
  function injectApiDataToPages(apiPageData) {
    //console.log("injectApiDataToPages", apiPageData);

    if (isTerminal) {
      //const localPageData = { ...pageData };
      if (pageData && apiPageData) {
        //console.log("xxxxxx", pageData, apiPageData.length);
        for (let index = 0; index < pageData.length; index++) {
          //const dataItem = pageData[index];
          //console.log("xxx", pageData[index]);

          const apiData = apiPageData.find((item) => item.pageId === pageData[index].pageId);
          //console.log("xx", apiData);

          if (apiData) {
            //console.log(apiData.title, apiData.subtitle);
            if (!Helper.isNullOrEmpty(apiData.title)) {
              pageData[index]["title"] = apiData.title;
            }

            if (!Helper.isNullOrEmpty(apiData.subtitle)) {
              pageData[index]["subtitle"] = apiData.subtitle;
            }
          }
        }
        setPageData(pageData);
      }
    }
  }

  function canShowScreenSaver() {
    // 1. Kontrola, zda je šetřič vůbec povolen
    if (!terminalSettingsState.isScreenSaverEnabled) {
      logd("canShowScreenSaver isScreenSaverEnabled:false");
      return false;
    }

    // 2. Získání aktuálního času v pražském čase
    // Intl.DateTimeFormat zajistí správné HH:mm:ss i na RPi
    const now = new Date().toLocaleTimeString("cs-CZ", {
      timeZone: "Europe/Prague",
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

    // 3. Formátování startovního a koncového času na HH:mm:ss
    const pad = (num) => String(num).padStart(2, "0");

    const start = `${pad(terminalSettingsState.screenSaverHourStart)}:${pad(terminalSettingsState.screenSaverMinuteStart)}:00`;
    const end = `${pad(terminalSettingsState.screenSaverHourEnd)}:${pad(terminalSettingsState.screenSaverMinuteEnd)}:00`;

    // 4. Použití předchozí logiky pro kontrolu intervalu
    const show = checkTimeInInterval(now, start, end);

    logd(`canShowScreenSaver: ScreenSaver: Start: ${start}, End: ${end}, Now: ${now} -> Show: ${show}`);
    return show;
  }

  //pro urceni zda je interval uvnitr
  function checkTimeInInterval(checkTime, startTime, endTime) {
    // Pomocná funkce pro převod "HH:mm:ss" na sekundy (0 - 86399)
    const toSeconds = (timeStr) => {
      const [h, m, s] = timeStr.split(":").map(Number);
      return h * 3600 + m * 60 + s;
    };

    const start = toSeconds(startTime);
    const end = toSeconds(endTime);
    const check = toSeconds(checkTime);

    if (end > start) {
      // Standardní interval (např. 08:00 - 16:00)
      return check >= start && check < end;
    } else {
      // Interval přes půlnoc (např. 22:00 - 06:00)
      return check >= start || check < end;
    }
  }

  const formatTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    const seconds = now.getSeconds().toString().padStart(2, "0");
    const milliseconds = now.getMilliseconds().toString().padStart(3, "0");
    return `${hours}:${minutes}:${seconds}.${milliseconds}`;
  };

  const logd = (...args) => {
    console.log(...args);
    const formattedLine = formatLine(...args);
    setLogText(addLine(formattedLine));
  };

  const loge = (...args) => {
    console.error(...args);
    const formattedLine = formatLine(...args);
    setLogText(addLine(formattedLine, "ERROR"));
  };

  function formatLine(...args) {
    return args.map((arg) => (typeof arg === "object" ? JSON.stringify(arg, null, 2) : arg)).join(" ");
  }

  function addLine(item, type = "DEBUG") {
    const line = (prevText) => `${prevText}[${formatTime()}] ${type}: ${item}\n`;
    return line;
  }

  return (
    <FormContext.Provider
      value={{
        currentBtnPageIndex,
        selectedButtonId,
        validate,
        pageData,
        getPageData,
        getButtonsData,
        //getApiPage,
        navigateNext,
        navigateHome,
        navigateBackButtons,
        navigateNextButtons,
        showBackButton,
        setData,
        handleInput,
        state,
        terminalSettingsState,
        canShowScreenSaver,
        loaded: loaded,
        isTaskRunning,
        isEmptyState,
        lastError,
        handleInputWithKeyboard,
        navigateNextInsuranceButton,
        isDebugMode,
        handleDebug,
        selectedPageIndex,
        isInErrorState,
        navigateToServicePage,
        handleAnimationDisabled,
        setDarkMode,
        runRestartOrShutdown,
        isAnimationDisabled,
        isDebugModeBackBtnClickable,
        logText,
        logd,
        loge,
        sendLogToApi,
        storageUid,
        stepCount: pageData.length - 1,
        beep,
        axiosTerminalAppInstance,
        instance,
        getUid,
        isSoundEnabled,
        setSoundEnabledApi,
        isPhotoCaptureTestEnabled,
        setPhotoCaptureTestEnabledApi,
        isWithoutCardEnabled,
        setWithouCardApi,
        eraseTerminalLog,
        getTerminalLog,
        getTerminalHwInfo,
        isInfoPanel,
        infoPanelState,
        playGong,
        setLedTo,
        isCursorEnabled,
        setCursorEnabledApi,
        setBrightnessTo,
      }}
    >
      {children}
    </FormContext.Provider>
  );
}
